import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Popover,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchEconomyConfig, fetchWallet, giveAward } from '../reducers/economyReducer';
import { notify } from '../reducers/notificationReducer';
import getErrorMsg from '../utils/getErrorMsg';
import Medal from './Medal';
import type { AwardDefinition, EconomyState, UserState } from '../types/common';

interface AwardTrayProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  postId: string;
}

/**
 * Give an award. A floating surface, so glass chrome is correct here (content
 * cards stay solid).
 *
 * Spending real balance is never one tap: picking an award arms it, and a
 * second explicit press sends it.
 */
const AwardTray: FC<AwardTrayProps> = ({ anchorEl, onClose, postId }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user as UserState | null);
  const economy = useAppSelector((state) => state.economy as EconomyState);

  const [selected, setSelected] = useState<AwardDefinition | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [sending, setSending] = useState(false);

  const open = Boolean(anchorEl);
  const coins = economy.balances?.coins?.available ?? 0;
  const awards = economy.config?.awards ?? [];

  useEffect(() => {
    if (!open || !user) return;
    if (!economy.config) dispatch(fetchEconomyConfig());
    if (!economy.balances) dispatch(fetchWallet());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  // Reset the arming state whenever the tray reopens.
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setSending(false);
    }
  }, [open]);

  const handleSend = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await dispatch(giveAward(postId, selected.key, selected.name, anonymous));
      onClose();
    } catch (err) {
      const message = getErrorMsg(err);
      dispatch(
        notify(
          message.toLowerCase().includes('enough')
            ? `Not enough Coins for ${selected.name}.`
            : message,
          'error'
        )
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { p: 1.5, maxWidth: 340 } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography
          sx={{
            fontFamily: theme.tokens.typography.monoFontFamily,
            fontSize: '10.5px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: theme.tokens.colors.text3,
          }}
        >
          Give an award
        </Typography>
        <Typography
          sx={{
            fontFamily: theme.tokens.typography.monoFontFamily,
            fontSize: '11.5px',
            color: theme.palette.primary.main,
          }}
        >
          {coins.toLocaleString()} coins
        </Typography>
      </Box>

      {awards.length === 0 ? (
        <Box sx={{ display: 'grid', placeItems: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}>
          {awards.map((award) => {
            const affordable = coins >= award.coinCost;
            const isSelected = selected?.key === award.key;
            return (
              <Tooltip
                key={award.key}
                title={
                  affordable
                    ? award.description
                    : `${award.description} — you need ${award.coinCost - coins} more Coins`
                }
              >
                <Box component="span">
                  <ButtonBase
                    onClick={() => setSelected(isSelected ? null : award)}
                    disabled={!affordable}
                    aria-pressed={isSelected}
                    aria-label={`${award.name}, ${award.coinCost} coins`}
                    sx={{
                      flexDirection: 'column',
                      gap: 0.5,
                      width: 82,
                      p: 1,
                      borderRadius: 'var(--sh-md)',
                      // Unaffordable awards stay visible but obviously out of
                      // reach — the same treatment locked reactions get.
                      opacity: affordable ? 1 : 0.45,
                      filter: affordable ? 'none' : 'grayscale(1)',
                      border: `1px solid ${
                        isSelected ? theme.tokens.colors.redLine : 'transparent'
                      }`,
                      backgroundColor: isSelected
                        ? theme.tokens.colors.redSoft
                        : 'transparent',
                    }}
                  >
                    <Medal tier={award.tier} size={40} showTag={false}>
                      <EmojiEventsIcon sx={{ fontSize: 18 }} />
                    </Medal>
                    <Typography
                      sx={{
                        fontFamily: theme.tokens.typography.sansFontFamily,
                        fontWeight: theme.tokens.typography.fontWeightBold,
                        fontSize: '11px',
                        lineHeight: 1.2,
                        textAlign: 'center',
                      }}
                    >
                      {award.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: theme.tokens.typography.monoFontFamily,
                        fontSize: '10.5px',
                        color: theme.tokens.colors.text3,
                      }}
                    >
                      {award.coinCost}
                    </Typography>
                  </ButtonBase>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Switch
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
          inputProps={{ 'aria-label': 'Award anonymously' }}
        />
        <Typography sx={{ fontSize: '12.5px', color: theme.tokens.colors.text3, flex: 1 }}>
          Award anonymously
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!selected || sending}
          onClick={handleSend}
        >
          {sending ? 'Sending…' : selected ? `Give · ${selected.coinCost}` : 'Give'}
        </Button>
      </Box>
    </Popover>
  );
};

export default AwardTray;
